import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { postAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { 
  Plus, Heart, MessageCircle, Pin, Send, MoreVertical, 
  Edit, Trash2, X, FileText, Megaphone, HelpCircle, MessageSquare
} from 'lucide-react';
import Pagination from '../components/Pagination';
import { SkeletonFeed } from '../components/LoadingSkeleton';
import './Posts.css';

function Posts() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Fetch posts
  const { data: postsData, isLoading, error } = useQuery({
    queryKey: ['posts', page, limit],
    queryFn: () => postAPI.getAll({ page, limit }).then(res => res.data),
    keepPreviousData: true,
  });

  // Create post mutation
  const createMutation = useMutation({
    mutationFn: (data) => postAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['posts']);
      setShowCreateModal(false);
    },
  });

  // Like post mutation
  const likeMutation = useMutation({
    mutationFn: (id) => postAPI.like(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['posts']);
    },
  });

  // Delete post mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => postAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['posts']);
      setSelectedPost(null);
    },
  });

  // Add comment mutation
  const commentMutation = useMutation({
    mutationFn: ({ postId, content }) => postAPI.addComment(postId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries(['posts']);
      setNewComment('');
    },
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: ({ postId, commentId }) => postAPI.deleteComment(postId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['posts']);
    },
  });

  const posts = postsData?.posts || [];

  const typeIcons = {
    announcement: <Megaphone size={16} />,
    update: <FileText size={16} />,
    discussion: <MessageSquare size={16} />,
    question: <HelpCircle size={16} />,
  };

  const typeColors = {
    announcement: '#dc2626',
    update: '#3b82f6',
    discussion: '#10b981',
    question: '#f59e0b',
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const handleCommentSubmit = (postId) => {
    if (newComment.trim()) {
      commentMutation.mutate({ postId, content: newComment });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="posts-page">
      {/* Header */}
      <div className="posts-header">
        <div className="header-left">
          <h1><FileText size={28} /> Updates</h1>
          <p className="posts-subtitle">Team announcements and discussions</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} /> New Post
        </button>
      </div>

      {/* Posts Feed */}
      {isLoading ? (
        <SkeletonFeed count={4} />
      ) : error ? (
        <div className="error-state">Failed to load posts. Please try again.</div>
      ) : posts.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <h3>No posts yet</h3>
          <p>Be the first to share an update with your team.</p>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /> Create Post
          </button>
        </div>
      ) : (
        <div className="posts-feed">
          {posts.map(post => (
            <div key={post.id} className={`post-card ${post.is_pinned ? 'pinned' : ''}`}>
              {post.is_pinned && (
                <div className="pinned-badge">
                  <Pin size={14} /> Pinned
                </div>
              )}
              <div className="post-header">
                <div className="author-info">
                  {post.author_photo ? (
                    <img src={post.author_photo} alt={post.author_name} className="author-avatar" />
                  ) : (
                    <div className="author-avatar-placeholder">
                      {post.author_name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  <div className="author-details">
                    <span className="author-name">{post.author_name}</span>
                    <span className="post-time">{formatDate(post.created_at)}</span>
                  </div>
                </div>
                <div className="post-type" style={{ backgroundColor: typeColors[post.type] }}>
                  {typeIcons[post.type]}
                  {post.type}
                </div>
              </div>
              
              {post.title && <h2 className="post-title">{post.title}</h2>}
              <p className="post-content">{post.content}</p>
              
              <div className="post-actions">
                <button 
                  className={`action-btn ${post.is_liked ? 'liked' : ''}`}
                  onClick={() => likeMutation.mutate(post.id)}
                >
                  <Heart size={18} fill={post.is_liked ? '#ef4444' : 'none'} />
                  <span>{post.likes_count}</span>
                </button>
                <button 
                  className="action-btn"
                  onClick={() => setSelectedPost(selectedPost?.id === post.id ? null : post)}
                >
                  <MessageCircle size={18} />
                  <span>{post.comments_count}</span>
                </button>
                {(post.author_id === user?.id || isAdmin) && (
                  <button 
                    className="action-btn delete"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this post?')) {
                        deleteMutation.mutate(post.id);
                      }
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>

              {/* Comments Section */}
              {selectedPost?.id === post.id && (
                <PostComments 
                  postId={post.id}
                  user={user}
                  isAdmin={isAdmin}
                  newComment={newComment}
                  setNewComment={setNewComment}
                  onSubmit={() => handleCommentSubmit(post.id)}
                  onDeleteComment={(commentId) => {
                    if (window.confirm('Delete this comment?')) {
                      deleteCommentMutation.mutate({ postId: post.id, commentId });
                    }
                  }}
                  isLoading={commentMutation.isPending}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {postsData?.pagination && (
        <Pagination
          page={page}
          totalPages={postsData.pagination.total_pages}
          total={postsData.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
        />
      )}

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

// Post Comments Component
function PostComments({ postId, user, isAdmin, newComment, setNewComment, onSubmit, onDeleteComment, isLoading }) {
  const { data } = useQuery({
    queryKey: ['post', postId],
    queryFn: () => postAPI.getById(postId).then(res => res.data),
  });

  const comments = data?.post?.comments || [];

  return (
    <div className="comments-section">
      <div className="comments-list">
        {comments.length === 0 ? (
          <p className="no-comments">No comments yet. Be the first to comment!</p>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="comment">
              <div className="comment-avatar">
                {comment.author_photo ? (
                  <img src={comment.author_photo} alt="" />
                ) : (
                  <div className="avatar-placeholder">
                    {comment.first_name?.charAt(0) || 'U'}
                  </div>
                )}
              </div>
              <div className="comment-body">
                <div className="comment-header">
                  <span className="comment-author">{comment.first_name} {comment.last_name}</span>
                  <span className="comment-time">
                    {new Date(comment.created_at).toLocaleDateString()}
                  </span>
                  {(comment.user_id === user?.id || isAdmin) && (
                    <button
                      className="comment-delete-btn"
                      onClick={() => onDeleteComment(comment.id)}
                      title="Delete comment"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <p className="comment-text">{comment.content}</p>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="comment-input">
        <input
          type="text"
          placeholder="Write a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && onSubmit()}
        />
        <button onClick={onSubmit} disabled={isLoading || !newComment.trim()}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

// Create Post Modal
function CreatePostModal({ onClose, onSubmit, isLoading, isAdmin }) {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'update',
    is_pinned: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.content.trim()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content post-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Post</h2>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Post Type</label>
            <div className="type-selector">
              {['update', 'announcement', 'discussion', 'question'].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`type-option ${formData.type === type ? 'active' : ''}`}
                  onClick={() => setFormData({ ...formData, type })}
                  style={{ borderColor: formData.type === type ? '#6366f1' : 'transparent' }}
                >
                  {type === 'announcement' && <Megaphone size={18} />}
                  {type === 'update' && <FileText size={18} />}
                  {type === 'discussion' && <MessageSquare size={18} />}
                  {type === 'question' && <HelpCircle size={18} />}
                  <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Title (optional)</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Add a title..."
            />
          </div>
          <div className="form-group">
            <label>Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="What's on your mind?"
              rows={5}
              required
            />
          </div>
          {isAdmin && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                />
                <Pin size={16} />
                Pin this post
              </label>
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Posts;
